import { request } from './request'

/**
 * 获取会员用户信息
 * @returns {Promise<Object>} 用户信息
 */
export async function fetchUserInfo() {
  const res = await request.post('/api/member/home/getUserInfo')
  return res.data || {}
}

/**
 * 获取企业信息
 * @returns {Promise<Object>} 企业信息
 */
export async function fetchCompanyInfo() {
  const res = await request.post('/api/company/home/getCompanyInfo')
  return res.data || {}
}

/**
 * 获取会员首页数据
 * @returns {Promise<Object>} 首页统计数据
 */
export async function getMemberIndexInfo() {
  const res = await request.post('/api/member/home/getIndexInfo')
  return res.data || {}
}

/**
 * 获取企业首页数据
 * @returns {Promise<Object>} 首页统计数据
 */
export async function getCompanyIndexInfo() {
  const res = await request.post('/api/company/home/getIndexInfo')
  return res.data || {}
}

/**
 * 获取反馈列表
 * @param {Object} params - { pageNum, pageSize }
 * @returns {Promise<Object>} { rows, total }
 */
export async function fetchFeedbackList(params) {
  const res = await request.get('/api/feedback/list', { params })
  return res.data || res || {}
}

/**
 * 修改密码
 * @param {String} mobile - 手机号
 * @param {String} code - 验证码
 * @param {String} newPassword - 新密码
 * @returns {Promise<Object>} 结果
 */
export async function changePassword(mobile, smsCode, newPassword) {
  const res = await request.post('/api/member/home/changePassword', {
    mobile,
    smsCode,
    password: newPassword
  })
  return res
}

/**
 * 发送短信验证码（会员）
 * @param {String} mobile - 手机号
 * @returns {Promise<Object>} 结果
 */
export async function sendSmsCode(mobile, param, type) {
  if (type === 'company') {
    const res = await request.post('/api/company/getMessage', {
      mobile,
      captchaCode: param.captchaCode,
      uuid: param.uuid,
      areaCode: '86'
    })
    return res
  }
  const res = await request.post('/api/member/getMessage', {
    mobile,
    captchaCode: param.captchaCode,
    uuid: param.uuid,
    areaCode: '86'
  })
  return res
}

/**
 * 获取我的新闻列表
 * @param {Object} params - { queryStatus, pageNum, pageSize }
 * @param {String} userType - 'company' 或 'personal'
 * @returns {Promise<Object>} { records, total }
 */
export async function fetchMyNewsList(params, userType) {
  const url = userType === 'company'
    ? '/api/company/news/list'
    : '/api/member/news/list'
  const res = await request.post(url, params)
  return res.data || {}
}

/**
 * 获取会员互动列表（评论/收藏/点赞）
 * @param {String} apiType - 'comment' | 'favorite' | 'like'
 * @param {Object} params - { pageNum, pageSize }
 * @returns {Promise<Object>} { records, total }
 */
export async function fetchMemberList(apiType, params) {
  const urlMap = {
    comment: '/api/member/comment/list',
    favorite: '/api/member/newsFavorite/list',
    like: '/api/member/newsLike/list'
  }
  const res = await request.post(urlMap[apiType], params)
  return res.data || {}
}

/**
 * 删除评论
 * @param {Number|String} id - 评论 ID
 * @returns {Promise<Object>} 结果
 */
export async function deleteComment(id) {
  const res = await request.post('/api/member/comment/del', { id })
  return res
}

/**
 * 取消收藏
 * @param {Number|String} newsId - 新闻 ID
 * @returns {Promise<Object>} 结果
 */
export async function cancelFavorite(newsId) {
  const res = await request.post('/api/member/newsFavorite/favorite', {
    newsId,
    status: 0
  })
  return res
}

/**
 * 取消点赞
 * @param {Number|String} newsId - 新闻 ID
 * @returns {Promise<Object>} 结果
 */
export async function cancelLike(newsId) {
  const res = await request.post('/api/member/newsLike/like', {
    newsId,
    status: 0
  })
  return res
}

/**
 * 上传文件（通用图片上传）
 * @param {File} file - 文件对象
 * @returns {Promise<String>} 文件 URL
 */
export async function uploadFile(file) {
  const formData = new FormData()
  formData.append('file', file)
  const res = await request.post('/api/common/uploadFile/picFile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return res.data
}

// ========== 会员个人信息 ==========

/**
 * 修改头像
 */
export async function changeAvatar(avatar) {
  const res = await request.post('/api/member/home/changeAvatar', { avatar })
  return res
}

/**
 * 修改昵称
 */
export async function changeNickname(nickname) {
  const res = await request.post('/api/member/home/changeNickname', { nickname })
  return res
}

/**
 * 修改手机号（会员）
 */
export async function changePhone(code, newMobile, password) {
  const res = await request.post('/api/member/home/changePhone', {
    code, newMobile, password
  })
  return res
}

/**
 * 提交实名认证信息
 */
export async function submitIdInfo(params) {
  const res = await request.post('/api/member/home/submitIdInfo', params)
  return res
}

// ========== 企业信息 ==========

/**
 * 修改企业头像
 */
export async function updateCompanyAvatar(avatar) {
  const res = await request.post('/api/company/home/updateAvatar', { avatar })
  return res
}

/**
 * 修改企业简称
 */
export async function updateShortName(companyShortName) {
  const res = await request.post('/api/company/home/updateShortName', { companyShortName })
  return res
}

/**
 * 修改企业手机号
 */
export async function changeCompanyPhone(code, newMobile, password) {
  const res = await request.post('/api/company/home/changePhone', {
    code, newMobile, password
  })
  return res
}

/**
 * 修改企业密码
 */
export async function changeCompanyPassword(mobile, smsCode, newPassword) {
  const res = await request.post('/api/company/home/changePassword', {
    mobile, smsCode, password: newPassword
  })
  return res
}

/**
 * 提交企业认证信息
 */
export async function completeCompany(params) {
  const res = await request.post('/api/company/home/completeCompany', params)
  return res
}

// ========== 发布文章 ==========

/**
 * 检测标题是否重复
 */
export async function checkNewsTitle(title, userType, id = null) {
  const url = userType === 'company'
    ? '/api/company/news/checkTitle'
    : '/api/member/news/checkTitle'
  const params = { title }
  // 如果 id 存在，添加到请求参数中（用于编辑模式）
  if (id) {
    params.id = id
  }
  const res = await request.post(url, params)
  return res.data === true || res.data === 'true' || res.data === 1
}

/**
 * 提交文章审核
 */
export async function submitNewsAudit(params, userType) {
  const url = userType === 'company'
    ? '/api/company/news/submitAudit'
    : '/api/member/news/submitAudit'
  const res = await request.post(url, params)
  return res
}
